import React, { useState, useRef, useEffect, useMemo } from 'react';
import { doc, updateDoc, arrayUnion, getDoc, writeBatch, serverTimestamp, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { normaliseForSearch } from '../../utils/diacritics';
import { getInverseKey } from '../../utils/referenceInverse';
import keyRegistryData from '../../config/keyRegistry.json';
import { getKeysForLanguage } from '../../utils/keyRegistry';
import { usePreferences } from '../../context/UserPreferencesContext';
import './DisplayItemsEditor.css';

/**
 * Build a lookup map from the key registry.
 * Each key has: category, valueLabel, options (if restricted), keyNote, etc.
 */
const KEY_REGISTRY = {};
keyRegistryData.keys.forEach((entry) => {
  KEY_REGISTRY[entry.key] = entry;
});

// Keys excluded from the dropdown because they are handled by QuickAddChips or auto-generated
const EXCLUDED_KEYS = new Set([
  'meaning', 'meaning (French)', 'gender', 'register', 'word type', 'synonym is', 'antonym is',
  'formal variant is', 'informal variant is', 'is a', 'is used',
  'literal meaning', 'plural form', 'feminine form', 'masculine form', 'conjugated form of',
  // Also exclude quickAddChip and autoGenerated entries dynamically
  ...keyRegistryData.keys.filter(k => k.quickAddChip || k.autoGenerated).map(k => k.key),
]);

/**
 * DisplayItemsEditor — form to add a new displayItem to a dictionary entry.
 * Uses the key registry to determine value field behaviour:
 *   - plainText → free text input
 *   - dictionaryReference → search-as-you-type, must match existing entry
 *   - restrictedDropdown → dropdown with fixed options
 *   - multiReference → multiple search fields (2+ entries)
 *
 * Props:
 *   entryId — Firestore doc ID
 *   entryCanonicalForm — the source entry's canonical form, needed to build the
 *     inverse item for bidirectional reference writes (non-staging mode only).
 *   onSaved(item) — called after item is saved
 *   onCancel() — called on cancel
 *   stagingMode — when true, skip Firestore write and call onSaved(item) directly
 */
function DisplayItemsEditor({ entryId, entryCanonicalForm, onSaved, onCancel, stagingMode }) {
  const { targetLanguage } = usePreferences();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const keyRef = useRef(null);

  // Dictionary reference state
  const [refResults, setRefResults] = useState([]);
  const [refSelected, setRefSelected] = useState(null); // { id, canonicalForm }
  const [showRefResults, setShowRefResults] = useState(false);
  const refDebounceRef = useRef(null);

  // Multi-reference state (for "made up of")
  const [components, setComponents] = useState(['', '']); // min 2 fields
  const [componentEntries, setComponentEntries] = useState([null, null]); // resolved entries
  const [activeComponentIndex, setActiveComponentIndex] = useState(null);
  const [componentResults, setComponentResults] = useState([]);
  const componentDebounceRef = useRef(null);

  // Restricted dropdown state
  const [dropdownValue, setDropdownValue] = useState('');

  // Get the registry entry for the selected key
  const registryEntry = KEY_REGISTRY[key] || null;
  const category = registryEntry?.category || 'plainText';

  // Available keys for the current target language, excluding chip/auto-generated keys
  const availableKeys = useMemo(
    () => getKeysForLanguage(targetLanguage).filter((k) => !EXCLUDED_KEYS.has(k.key)).map((k) => k.key),
    [targetLanguage]
  );

  // Filter key suggestions
  const filteredSuggestions = availableKeys.filter(
    (s) => s.toLowerCase().includes(key.toLowerCase()) && s.toLowerCase() !== key.toLowerCase()
  );

  // Reset value state when key changes
  useEffect(() => {
    setValue('');
    setRefSelected(null);
    setRefResults([]);
    setShowRefResults(false);
    setDropdownValue('');
    setComponents(['', '']);
    setComponentEntries([null, null]);
    setError('');
  }, [key]);

  // --- Dictionary reference search ---
  useEffect(() => {
    if (category !== 'dictionaryReference') return;
    if (value.trim().length < 2) {
      setRefResults([]);
      setShowRefResults(false);
      return;
    }

    clearTimeout(refDebounceRef.current);
    refDebounceRef.current = setTimeout(() => {
      searchDictionary(value.trim()).then((entries) => {
        setRefResults(entries);
        setShowRefResults(true);
      });
    }, 300);

    return () => clearTimeout(refDebounceRef.current);
  }, [value, category]);

  // --- Component search (multi-reference) ---
  useEffect(() => {
    if (category !== 'multiReference' || activeComponentIndex === null) return;
    const term = components[activeComponentIndex];
    if (!term || term.trim().length < 2) {
      setComponentResults([]);
      return;
    }

    clearTimeout(componentDebounceRef.current);
    componentDebounceRef.current = setTimeout(() => {
      searchDictionary(term.trim()).then((entries) => {
        setComponentResults(entries);
      });
    }, 300);

    return () => clearTimeout(componentDebounceRef.current);
  }, [components, activeComponentIndex, category]);

  const searchDictionary = async (term) => {
    const normalised = normaliseForSearch(term);
    const endStr = normalised.slice(0, -1) + String.fromCharCode(normalised.charCodeAt(normalised.length - 1) + 1);

    try {
      const q = query(
        collection(db, 'dictionary'),
        where('canonicalFormNormalised', '>=', normalised),
        where('canonicalFormNormalised', '<', endStr),
        orderBy('canonicalFormNormalised'),
        limit(8)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('Search error:', e);
      return [];
    }
  };

  // --- Handlers ---
  const handleKeyFocus = () => setShowSuggestions(true);
  const handleKeyBlur = () => setTimeout(() => setShowSuggestions(false), 150);

  const handleSuggestionClick = (suggestion) => {
    setKey(suggestion);
    setShowSuggestions(false);
  };

  const handleRefSelect = (entry) => {
    setRefSelected(entry);
    setValue(entry.canonicalForm);
    setShowRefResults(false);
    setError('');
  };

  const handleComponentSelect = (entry, index) => {
    const newComponents = [...components];
    newComponents[index] = entry.canonicalForm;
    setComponents(newComponents);

    const newEntries = [...componentEntries];
    newEntries[index] = entry;
    setComponentEntries(newEntries);

    setComponentResults([]);
    setActiveComponentIndex(null);
  };

  const addComponentField = () => {
    setComponents([...components, '']);
    setComponentEntries([...componentEntries, null]);
  };

  const removeComponentField = (index) => {
    if (components.length <= 2) return;
    setComponents(components.filter((_, i) => i !== index));
    setComponentEntries(componentEntries.filter((_, i) => i !== index));
  };

  // --- Validation ---
  const canSave = () => {
    if (!key.trim()) return false;

    switch (category) {
      case 'plainText':
        return value.trim().length > 0;
      case 'dictionaryReference':
        return refSelected !== null;
      case 'restrictedDropdown':
        return dropdownValue.length > 0;
      case 'multiReference':
        return componentEntries.every((e) => e !== null) && componentEntries.length >= 2;
      default:
        return value.trim().length > 0;
    }
  };

  // --- Save ---
  const handleSave = async () => {
    if (!canSave()) return;
    setSaving(true);
    setError('');

    let item;

    switch (category) {
      case 'dictionaryReference': {
        // Fetch the referenced entry's first displayItem for auto-snapshot note
        let autoNote = null;
        try {
          const refDoc = await getDoc(doc(db, 'dictionary', refSelected.id));
          if (refDoc.exists()) {
            const data = refDoc.data();
            const firstItem = (data.displayItems || [])[0];
            if (firstItem) {
              if (firstItem.type === 'text' && firstItem.value) {
                autoNote = firstItem.value;
              } else if (firstItem.type === 'label' && firstItem.text) {
                autoNote = firstItem.text;
              }
            } else if (data.meaningEnglish) {
              autoNote = data.meaningEnglish;
            }
          }
        } catch (e) {
          // Non-critical — skip auto-note
        }

        item = {
          type: 'text',
          key: key.trim(),
          value: refSelected.canonicalForm,
          entryId: refSelected.id,
          note: autoNote,
        };
        break;
      }

      case 'restrictedDropdown': {
        // For gender, generate the natural reading: "is a masculine noun"
        let finalKey = key.trim();
        let finalValue = dropdownValue;
        if (key === 'gender') {
          finalKey = 'is a';
          finalValue = `${dropdownValue} noun`;
        }
        item = { type: 'text', key: finalKey, value: finalValue, entryId: null, note: null };
        break;
      }

      case 'multiReference': {
        const comps = componentEntries.map((entry) => ({
          entryId: entry.id,
          label: entry.canonicalForm,
        }));
        item = { type: 'composition', key: key.trim(), components: comps, note: null };
        break;
      }

      default: {
        // Plain text
        item = { type: 'text', key: key.trim(), value: value.trim(), entryId: null, note: null };
        break;
      }
    }

    if (stagingMode) {
      // Staging mode: skip Firestore write, call onSaved directly
      onSaved && onSaved(item);
      setKey('');
      setValue('');
      setRefSelected(null);
      setDropdownValue('');
      setComponents(['', '']);
      setComponentEntries([null, null]);
      setSaving(false);
      return;
    }

    try {
      // Bidirectional add: if this is a dictionaryReference item (has an
      // entryId) whose key has an inverse, mirror the inverse onto the
      // referenced entry in a single batch.
      const inverseKey = getInverseKey(item.key);
      if (inverseKey && item.entryId && entryCanonicalForm) {
        const refRef = doc(db, 'dictionary', item.entryId);
        const refSnap = await getDoc(refRef);
        if (refSnap.exists()) {
          const refData = refSnap.data();
          const inverseItem = { type: 'text', key: inverseKey, value: entryCanonicalForm, entryId: entryId, note: null };
          const existing = (refData.displayItems || []).some(
            (di) => di.type === 'text' && di.key === inverseKey && di.entryId === entryId && di.value === entryCanonicalForm
          );
          const batch = writeBatch(db);
          batch.update(doc(db, 'dictionary', entryId), { displayItems: arrayUnion(item), lastUpdated: serverTimestamp() });
          if (!existing) {
            batch.update(refRef, { displayItems: arrayUnion(inverseItem), lastUpdated: serverTimestamp() });
          }
          await batch.commit();
          onSaved && onSaved(item);
          // Reset form
          setKey('');
          setValue('');
          setRefSelected(null);
          setDropdownValue('');
          setComponents(['', '']);
          setComponentEntries([null, null]);
          setSaving(false);
          return;
        }
      }

      const entryRef = doc(db, 'dictionary', entryId);
      await updateDoc(entryRef, {
        displayItems: arrayUnion(item),
        lastUpdated: serverTimestamp(),
      });
      onSaved && onSaved(item);
      // Reset form
      setKey('');
      setValue('');
      setRefSelected(null);
      setDropdownValue('');
      setComponents(['', '']);
      setComponentEntries([null, null]);
    } catch (e) {
      console.error('Error saving display item:', e);
      setError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  // --- Render value field based on category ---
  const renderValueField = () => {
    if (!key.trim()) {
      return (
        <input
          className="display-items-editor-input display-items-editor-value"
          type="text"
          placeholder="Select a key first"
          disabled
        />
      );
    }

    switch (category) {
      case 'dictionaryReference':
        return (
          <div className="display-items-editor-ref-wrap">
            <input
              className="display-items-editor-input display-items-editor-value"
              type="text"
              placeholder={registryEntry?.valueLabel || 'Search dictionary...'}
              value={value}
              onChange={(e) => { setValue(e.target.value); setRefSelected(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && canSave()) handleSave(); }}
              aria-label="Reference value"
              autoComplete="off"
            />
            {refSelected && (
              <span className="display-items-editor-ref-badge">✓ {refSelected.canonicalForm}</span>
            )}
            {!refSelected && value.trim().length >= 2 && refResults.length === 0 && showRefResults && (
              <span className="display-items-editor-ref-error">Not found in dictionary</span>
            )}
            {showRefResults && refResults.length > 0 && (
              <div className="display-items-editor-suggestions">
                {refResults.map((entry) => (
                  <button
                    key={entry.id}
                    className="display-items-editor-suggestion"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleRefSelect(entry)}
                  >
                    {entry.canonicalForm}
                    {entry.meaningEnglish && <span className="display-items-editor-suggestion-hint"> — {entry.meaningEnglish}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'restrictedDropdown':
        return (
          <div className="display-items-editor-dropdown-wrap">
            {(registryEntry?.options || []).map((opt) => (
              <button
                key={opt}
                className={`display-items-editor-dropdown-chip ${dropdownValue === opt ? 'active' : ''}`}
                onClick={() => setDropdownValue(opt)}
                type="button"
              >
                {opt}
              </button>
            ))}
          </div>
        );

      case 'multiReference':
        return (
          <div className="display-items-editor-multi-wrap">
            {components.map((comp, index) => (
              <div key={index} className="display-items-editor-multi-field">
                <input
                  className="display-items-editor-input display-items-editor-multi-input"
                  type="text"
                  placeholder={`Word ${index + 1}`}
                  value={comp}
                  onChange={(e) => {
                    const newComps = [...components];
                    newComps[index] = e.target.value;
                    setComponents(newComps);
                    // Clear resolved entry if text changes
                    const newEntries = [...componentEntries];
                    newEntries[index] = null;
                    setComponentEntries(newEntries);
                  }}
                  onFocus={() => setActiveComponentIndex(index)}
                  onBlur={() => setTimeout(() => setActiveComponentIndex(null), 200)}
                  autoComplete="off"
                />
                {componentEntries[index] && (
                  <span className="display-items-editor-multi-check">✓</span>
                )}
                {components.length > 2 && (
                  <button
                    className="display-items-editor-multi-remove"
                    onClick={() => removeComponentField(index)}
                    aria-label={`Remove word ${index + 1}`}
                  >×</button>
                )}
                {/* Show results dropdown for active component */}
                {activeComponentIndex === index && componentResults.length > 0 && (
                  <div className="display-items-editor-suggestions display-items-editor-multi-suggestions">
                    {componentResults.map((entry) => (
                      <button
                        key={entry.id}
                        className="display-items-editor-suggestion"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleComponentSelect(entry, index)}
                      >
                        {entry.canonicalForm}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              className="display-items-editor-multi-add"
              onClick={addComponentField}
              type="button"
            >+</button>
          </div>
        );

      default:
        // Plain text
        return (
          <input
            className="display-items-editor-input display-items-editor-value"
            type="text"
            placeholder={registryEntry?.valueLabel || 'Value'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSave()) handleSave(); }}
            aria-label="Annotation value"
          />
        );
    }
  };

  return (
    <div className="display-items-editor">
      <div className="display-items-editor-row">
        <div className="display-items-editor-key-wrap">
          <input
            ref={keyRef}
            className="display-items-editor-input display-items-editor-key"
            type="text"
            placeholder="Key (e.g. meaning, is a, formal variant is)"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onFocus={handleKeyFocus}
            onBlur={handleKeyBlur}
            aria-label="Annotation key"
            autoComplete="off"
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="display-items-editor-suggestions">
              {filteredSuggestions.map((s) => {
                const reg = KEY_REGISTRY[s];
                return (
                  <button
                    key={s}
                    className="display-items-editor-suggestion"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(s)}
                  >
                    <span>{s}</span>
                    {reg?.category === 'dictionaryReference' && <span className="display-items-editor-suggestion-badge">↗ ref</span>}
                    {reg?.category === 'restrictedDropdown' && <span className="display-items-editor-suggestion-badge">▼</span>}
                    {reg?.category === 'multiReference' && <span className="display-items-editor-suggestion-badge">⊕ multi</span>}
                  </button>
                );
              })}
            </div>
          )}
          {/* Key note indicator */}
          {registryEntry?.keyNote && (
            <span className="display-items-editor-keynote" title={registryEntry.keyNote}>ⓘ</span>
          )}
        </div>
        {renderValueField()}
      </div>

      {error && <p className="display-items-editor-error">{error}</p>}

      <div className="display-items-editor-actions">
        <button
          className="display-items-editor-save"
          onClick={handleSave}
          disabled={!canSave() || saving}
        >
          {saving ? 'Saving…' : 'Add'}
        </button>
        <button className="display-items-editor-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default DisplayItemsEditor;
